import React from 'react';
import DividerWithText from '../../../../../../components/DividerWithText';
import { Trans } from 'react-i18next';
import { CustomTextField } from '../../../../../../components/CustomTextField';
import { CustomSelect } from '../../../../../../components/CustomSelect';
import {EMPTY_DEFAULT_DATE, EMPTY_DEFAULT_DATE_TIME, EMPTY_DEFAULT_TIME, PERIOD_TIME_UNIT_CYCLE} from './Model/Models';
import CustomUserChip from '../../../../../../components/CustomUserChip';
import AttendanceCycleApp from "./Functionality/AttendanceCycleApp";
import {CustomSwitch} from "../../../../../../components/CustomSwitch";

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param item
 * @param setItem
 * @param onChange
 * @param emptyFields
 */
const AttendanceCycle = (
    {
        item,
        setItem,
        onChange,
        emptyFields,
        disabled = false
    }
) => {
    const {
        t,
        disabled: cycleDisabled,
        supervisorIsValidTextFrom,
        supervisorIsValidTextTo,
        adminIsValidTextFrom,
        adminIsValidTextTo,
        legendBackground,
        legendFont,
        renderDate,
        renderEndTime,
        RangeRow,
        handleChange
    } = AttendanceCycleApp({
        item,
        setItem,
        onChange
    })

    // Read-only users cannot edit anything; the cycle fields also follow the cycle switch
    const isDisabled = disabled || cycleDisabled;

    return (
        <>
            <div className='col-12'>
                <DividerWithText spacing='my-4' title={t('record_configuration')} />
            </div>
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("check_out_update_tolerance")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("prevent_duplicate_check_out_updates_within_the_specified_time_window")}
                </div>

                <div className="col-12 col-sm-4 pl-sm-0">
                    <CustomTextField
                        id='txt_ac_check_out_cool_down_minutes'
                        type='number'
                        length={2}
                        trans='check_out_cool_down_minutes'
                        name="checkOutCooldown"
                        value={item.checkOutCooldown}
                        onChange={onChange}
                        suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                        required={true}
                        emptyFields={emptyFields}
                        isValid={item.isValidCheckOutCooldown}
                        isValidText={'this_value_must_be_greater_than_0'}
                        helperText={'this_value_must_be_greater_than_0'}
                        disabled={disabled}
                    />
                </div>
            </div>

            <div className='col-12'>
                <DividerWithText spacing='my-4' title={t('period_review_control')}/>
            </div>

            <div className={"d-flex align-items-center justify-content-between"}>
                <div className={"col-12 col-sm-3 px-0 fs-14px ibl-font-w-600"}>
                    {t("manual_attendance_edits")}
                </div>
                <div>
                    <CustomSwitch
                        id="cb_ac_enable_attendance_cycle"
                        name="enableAttendanceCycle"
                        checked={item.enableAttendanceCycle}
                        showBorder={false}
                        disabled={disabled}
                        onChange={(e) => handleChange({ target: { name: e.target.name, value: !item.enableAttendanceCycle } })}
                    />
                </div>
            </div>

            <div className="col-12 col-sm-3">
                <span className={"fs-12px font-color-shades-950"}> {t("attendance_cycle_legend")}</span>
            </div>

            <div className='col-12'>
                <DividerWithText spacing='my-4' title={t('attendance_cycle_configuration')}/>
            </div>

            <div className="col-12 pb-4">
                <span className={"fs-14px font-color-shades-950 ibl-font-w-600"}> {t("cycle_frequency")}</span>
            </div>

            <div className='col-12'>
                <div className="row">
                    <div className='col-12 col-sm-3 pl-0'>
                        <CustomTextField
                            id='txt_ac_frequency_value'
                            type='number'
                            length={2}
                            trans='frequency_value'
                            name="frequencyValue"
                            placeholder={t('enter_a_number')}
                            value={item.frequencyValue}
                            onChange={onChange}
                            required={true}
                            emptyFields={emptyFields}
                            isValid={isDisabled ? true : item.isValidFrequencyValue}
                            isValidText={t("this_value_must_be_greater_than_0")}
                            helperText={t("this_value_must_be_greater_than_0")}
                            disabled={isDisabled}
                        />
                    </div>
                    <div className='col-12 col-sm-4 pl-0 pl-sm-0'>
                        <CustomSelect
                            id='slt_ac_time_unit'
                            name='frequencyTimeUnit'
                            trans='time_unit'
                            keyName='name'
                            list={PERIOD_TIME_UNIT_CYCLE}
                            value={item.frequencyTimeUnit}
                            onChange={onChange}
                            required={true}
                            emptyFields={emptyFields}
                            showNoSelectionOption={false}
                            disabled={isDisabled}
                        />
                    </div>
                </div>
            </div>

            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("deferred_period")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("number_of_deferred_periods_to_review_when_payment_processing_is_delayed")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className='col-12 col-sm-5 pl-0'>
                        <CustomTextField
                            id='txt_ac_number_of_deferred_periods'
                            type='number'
                            length={2}
                            trans='number_of_deferred_periods'
                            name="deferredPeriodValue"
                            value={item.deferredPeriodValue}
                            onChange={onChange}
                            suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('periods')}</p>}
                            disabled={isDisabled}
                            required={true}
                            emptyFields={emptyFields}
                        />
                    </div>
                </div>
            </div>

            <div className="p-3">
                <CustomUserChip
                    className={`m-0 w-100 p-0 justify-content-start ${legendBackground}`}
                    name={<Trans
                        i18nKey="review_period_will_run_from_x_to_ending_at_x"
                        values={{
                            startPeriod: renderDate(
                                item.isValidFrequencyValue,
                                item?.startPeriod,
                                EMPTY_DEFAULT_DATE_TIME
                            ),
                            endPeriod: renderDate(
                                item.isValidFrequencyValue,
                                item?.endPeriod,
                                EMPTY_DEFAULT_DATE
                            ),
                            endTime: renderEndTime(
                                item.isValidFrequencyValue,
                                item?.endPeriod,
                                EMPTY_DEFAULT_TIME
                            )
                        }}
                        components={{
                            strong: <span className={`fs-12px ibl-font-w-700 ${legendFont}`} />
                        }}
                    />
                    }
                    variant="large"
                />
            </div>

            {/* SUPERVISOR */}
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("supervisor_review_window")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("time_allocated_for_supervisors_to_review_and_submit_corrections")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className="row">
                        <div className='col-12 col-sm-6 pl-0'>
                            <CustomTextField
                                id='txt_ac_supervisor_review_period_from'
                                type='number'
                                length={3}
                                trans='days_before_period_starts'
                                name="supervisorReviewPeriodFrom"
                                placeholder={t('enter_a_number')}
                                value={item.supervisorReviewPeriodFrom}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                required={true}
                                emptyFields={emptyFields}
                                isValid={isDisabled ? true : item.isValidSupervisorRangeFrom && item.isValidSupervisorReviewPeriodFrom}
                                isValidText={supervisorIsValidTextFrom}
                                helperText={supervisorIsValidTextFrom}
                                disabled={isDisabled}
                            />
                        </div>
                        <div className='col-12 col-sm-6 pl-0 pl-sm-3'>
                            <CustomTextField
                                id='txt_ac_supervisor_review_period_to'
                                type='number'
                                length={3}
                                trans='days_before_period_ends'
                                name="supervisorReviewPeriodTo"
                                placeholder={t('enter_a_number')}
                                value={item.supervisorReviewPeriodTo}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                emptyFields={emptyFields}
                                required={true}
                                isValid={isDisabled ? true : item.isValidSupervisorRangeTo && item.isValidSupervisorReviewPeriodTo}
                                isValidText={supervisorIsValidTextTo}
                                helperText={supervisorIsValidTextTo}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ADMIN */}
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("admin_approval_window")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("time_allocated_for_administrators_to_review_and_approve_corrections")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className="row">
                        <div className='col-12 col-sm-6 pl-0'>
                            <CustomTextField
                                id='txt_ac_admin_approval_period_from'
                                type='number'
                                length={3}
                                trans='days_before_period_starts'
                                name="adminApprovalPeriodFrom"
                                placeholder={t('enter_a_number')}
                                value={item.adminApprovalPeriodFrom}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                required={true}
                                emptyFields={emptyFields}
                                isValid={isDisabled ? true : item.isValidAdminRangeFrom && item.isValidAdminApprovalPeriodFrom}
                                isValidText={adminIsValidTextFrom}
                                helperText={adminIsValidTextFrom}
                                disabled={isDisabled}
                            />
                        </div>
                        <div className='col-12 col-sm-6 pl-0 pl-sm-3'>
                            <CustomTextField
                                id='txt_ac_admin_approval_period_to'
                                type='number'
                                length={3}
                                trans='days_before_period_ends'
                                name="adminApprovalPeriodTo"
                                placeholder={t('enter_a_number')}
                                value={item.adminApprovalPeriodTo}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                emptyFields={emptyFields}
                                required={true}
                                isValid={isDisabled ? true : item.isValidAdminRangeTo && item.isValidAdminApprovalPeriodTo}
                                isValidText={adminIsValidTextTo}
                                helperText={adminIsValidTextTo}
                                disabled={isDisabled}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-3">
                <CustomUserChip
                    variant="large"
                    className={`m-0 w-100 p-0 justify-content-start ${legendBackground}`}
                    name={
                        <div className="d-flex flex-column gap-2">
                            <RangeRow
                                label={t("supervisor_review")}
                                start={renderDate(
                                    item?.isValidFrequencyValue &&
                                    item?.isValidSupervisorReviewPeriodFrom &&
                                    item?.isValidSupervisorRangeFrom,
                                    item?.supervisorReviewStartDate,
                                    EMPTY_DEFAULT_DATE_TIME
                                )}
                                end={renderDate(
                                    item?.isValidFrequencyValue &&
                                    item?.isValidSupervisorReviewPeriodFrom &&
                                    item?.isValidSupervisorReviewPeriodTo &&
                                    item?.isValidSupervisorRangeTo,
                                    item?.supervisorReviewEndDate,
                                    EMPTY_DEFAULT_DATE_TIME
                                )}
                            />

                            <RangeRow
                                label={t("admin_approval")}
                                start={renderDate(
                                    item?.isValidFrequencyValue &&
                                    item?.isValidAdminApprovalPeriodFrom &&
                                    item?.isValidAdminRangeFrom,
                                    item?.adminApprovalStartDate,
                                    EMPTY_DEFAULT_DATE_TIME
                                )}
                                end={renderDate(
                                    item?.isValidFrequencyValue &&
                                    item?.isValidAdminApprovalPeriodFrom &&
                                    item?.isValidAdminApprovalPeriodTo &&
                                    item?.isValidAdminRangeTo,
                                    item?.adminApprovalEndDate,
                                    EMPTY_DEFAULT_DATE_TIME
                                )}
                            />

                        </div>
                    }
                />
            </div>
        </>
    );
}

export default AttendanceCycle
